import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    Clock,
    Loader,
    Medal,
    RefreshCw,
    Search,
    ShieldCheck,
    Star,
    TrendingUp,
    Users,
} from 'lucide-react';
import { getOpaAttendantsRanking } from '../../services/apiService';
import AttendantModal from './AttendantModal';
import OpaAvatar from './OpaAvatar';

const QualityDashboard = ({ theme }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [rankingData, setRankingData] = useState([]);
    const [sectorsList, setSectorsList] = useState([]);
    const [selectedSector, setSelectedSector] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [companyMedianTma, setCompanyMedianTma] = useState(0);
    const [lastSyncInfo, setLastSyncInfo] = useState(null);
    const [methodology, setMethodology] = useState(null);
    const [selectedAttendant, setSelectedAttendant] = useState(null);

    const [selectedMonth, setSelectedMonth] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date.getMonth() + 1;
    });
    const [selectedYear, setSelectedYear] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        return date.getFullYear();
    });

    const months = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
    ].map((label, index) => ({ value: index + 1, label }));
    const currentYear = new Date().getFullYear();
    const years = [currentYear - 1, currentYear, currentYear + 1];

    const fetchRanking = async (force = false) => {
        setLoading(true);
        setError('');
        try {
            const data = await getOpaAttendantsRanking(selectedMonth, selectedYear, selectedSector, force);
            if (!data?.success) throw new Error('Resposta inválida ao carregar dados de qualidade.');
            setRankingData(data.ranking || []);
            setSectorsList(data.setoresList || []);
            setCompanyMedianTma(data.companyMedianTma || 0);
            setMethodology(data.methodology || null);
            setLastSyncInfo({
                cached: Boolean(data.cached),
                stale: Boolean(data.stale),
                generatedAt: data.generatedAt || null,
            });
        } catch (requestError) {
            console.error('Erro ao carregar ranking do OPA:', requestError);
            const detail = requestError.response?.data?.detail || requestError.message;
            setError(`Falha ao carregar dados do OPA. Detalhe: ${typeof detail === 'object' ? JSON.stringify(detail) : detail}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRanking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth, selectedYear, selectedSector]);

    const filteredRanking = useMemo(() => rankingData.filter(attendant => (
        attendant.name.toLowerCase().includes(searchTerm.toLowerCase())
    )), [rankingData, searchTerm]);

    const departmentRanks = useMemo(() => {
        if (selectedSector === 'all') return new Map();
        return new Map(rankingData.map((attendant, index) => [attendant.id, index + 1]));
    }, [rankingData, selectedSector]);

    const eligibleCount = rankingData.filter(attendant => !attendant.isProvisional).length;
    const isLegacyCache = methodology?.version === 'legacy-cache';

    const formatTma = (minutes) => {
        if (!Number.isFinite(Number(minutes)) || Number(minutes) <= 0) return '-';
        const value = Number(minutes);
        const wholeMinutes = Math.floor(value);
        const seconds = Math.round((value - wholeMinutes) * 60);
        return `${wholeMinutes}m ${seconds}s`;
    };

    const formatTmaDeviation = (deviation) => {
        const value = Number(deviation);
        if (!Number.isFinite(value)) return '';
        return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
    };

    const describeTmaDeviation = (tma, deviation) => {
        const value = Number(deviation);
        const tmaLabel = formatTma(tma);
        if (!Number.isFinite(value)) return `TMA mediano do atendente: ${tmaLabel}. A comparação com o grupo não está disponível.`;
        if (value === 0) return `TMA mediano do atendente: ${tmaLabel}. Igual à mediana do grupo comparável.`;
        return `TMA mediano do atendente: ${tmaLabel}. ${value < 0 ? 'Abaixo' : 'Acima'} da mediana do grupo comparável em ${Math.abs(value).toFixed(1)}%.`;
    };

    const getScoreBadge = (score) => {
        if (score >= 4.5) return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
        if (score >= 3) return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
        if (score >= 2) return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
    };

    const medalColor = rank => ({
        1: 'text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]',
        2: 'text-slate-300 drop-shadow-[0_0_8px_rgba(203,213,225,0.8)]',
        3: 'text-amber-600 drop-shadow-[0_0_8px_rgba(217,119,6,0.8)]',
    }[rank] || 'text-transparent');

    const provisionalReason = attendant => {
        const reasons = [];
        if (attendant.eligibilityReasons?.includes('MIN_ATTENDANCES')) {
            reasons.push(`menos de ${methodology?.minimumAttendances || 30} atendimentos`);
        }
        if (attendant.eligibilityReasons?.includes('MIN_EVALUATIONS')) {
            reasons.push(`menos de ${methodology?.minimumEvaluations || 5} avaliações`);
        }
        if (attendant.eligibilityReasons?.includes('MIN_PEER_GROUP')) {
            reasons.push('grupo comparável muito pequeno');
        }
        return reasons.join(' e ');
    };

    const openAttendant = (attendant, displayRank = attendant.rank) => {
        const actualRank = displayRank ? displayRank - 1 : null;
        setSelectedAttendant({
            ...attendant,
            rank: displayRank,
            methodologyVersion: methodology?.version || '2.0',
            actualRank,
            isTop3: !attendant.isProvisional && displayRank <= 3,
        });
    };

    return (
        <div className="p-4 sm:p-6 bg-white dark:bg-slate-800/50 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 flex items-center gap-2">
                        <TrendingUp className="h-6 w-6 text-blue-500" />
                        Qualidade & Eficiência
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        {isLegacyCache
                            ? 'Ranking histórico disponível; aguardando nova sincronização para aplicar a metodologia 2.0.'
                            : 'Ranking justo por qualidade ajustada (50%), produtividade (35%) e eficiência (15%).'}
                    </p>
                    {lastSyncInfo && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                            {lastSyncInfo.stale ? 'Último cache histórico disponível' : lastSyncInfo.cached ? 'Dados em cache' : 'Dados recém-sincronizados'}
                            {lastSyncInfo.generatedAt && ` • Atualizado em ${new Date(lastSyncInfo.generatedAt).toLocaleString('pt-BR')}`}
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2 bg-slate-50 dark:bg-slate-900/50 p-2 rounded-xl border border-slate-200 dark:border-slate-700">
                    <select value={selectedMonth} onChange={event => setSelectedMonth(Number(event.target.value))} className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                        {months.map(month => <option key={month.value} value={month.value}>{month.label}</option>)}
                    </select>
                    <select value={selectedYear} onChange={event => setSelectedYear(Number(event.target.value))} className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                        {years.map(year => <option key={year} value={year}>{year}</option>)}
                    </select>
                    <select value={selectedSector} onChange={event => setSelectedSector(event.target.value)} className="max-w-52 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                        <option value="all">Todos os setores</option>
                        {sectorsList.map(sector => <option key={sector.id} value={sector.id}>{sector.name}</option>)}
                    </select>
                    <button onClick={() => fetchRanking(true)} className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors" title="Ignorar cache e sincronizar">
                        <RefreshCw className="h-4 w-4" />
                        Sincronizar
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col justify-center items-center h-64">
                    <Loader className="animate-spin h-10 w-10 text-blue-500 mb-4" />
                    <p className="text-slate-500 dark:text-slate-400 animate-pulse">Sincronizando com OPA Suite...</p>
                </div>
            ) : error ? (
                <div className="flex flex-col justify-center items-center h-64 text-center text-red-600 bg-red-50 dark:bg-red-900/20 p-6 rounded-xl border border-red-100 dark:border-red-800/50">
                    <AlertTriangle className="w-12 h-12 mb-4" />
                    <h3 className="text-lg font-bold mb-2">Não foi possível gerar o ranking</h3>
                    <p className="max-w-xl text-sm">{error}</p>
                    <button onClick={() => fetchRanking()} className="mt-4 px-4 py-2 bg-red-100 text-red-700 dark:bg-red-800/50 dark:text-red-300 rounded-lg font-medium">Tentar novamente</button>
                </div>
            ) : rankingData.length === 0 ? (
                <div className="flex flex-col justify-center items-center h-64 text-center text-slate-500 dark:text-slate-400">
                    <Star className="w-12 h-12 mb-4 opacity-20" />
                    <h3 className="text-lg font-medium mb-1">Nenhum dado encontrado</h3>
                    <p className="text-sm">Não há atendimentos finalizados no período selecionado.</p>
                </div>
            ) : (
                <div className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                        {[
                            { label: 'TMA geral', value: formatTma(companyMedianTma), icon: Clock, iconClass: 'text-blue-500' },
                            { label: 'Elegíveis a medalha', value: eligibleCount, icon: ShieldCheck, iconClass: 'text-emerald-500' },
                            { label: 'Atendentes analisados', value: rankingData.length, icon: Users, iconClass: 'text-indigo-500' },
                        ].map(card => (
                            <div key={card.label} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{card.label}</p>
                                    <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">{card.value}</p>
                                </div>
                                <card.icon className={`w-7 h-7 ${card.iconClass}`} />
                            </div>
                        ))}
                    </div>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <input type="text" placeholder="Buscar atendente..." value={searchTerm} onChange={event => setSearchTerm(event.target.value)} className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 dark:text-slate-200 shadow-sm" />
                    </div>

                    <div className="overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                                <thead className="bg-slate-50 dark:bg-slate-900/50">
                                    <tr>
                                        {(isLegacyCache
                                            ? ['Rank', 'Atendente', 'Setor / TMA', 'Avaliação 40%', 'Volume 35%', 'TMA 25%', 'Nota final']
                                            : ['Rank', 'Atendente e amostra', 'Grupo / TMA', 'Qualidade 50%', 'Produtividade 35%', 'Eficiência 15%', 'Nota final']
                                        ).map((heading, index) => (
                                            <th key={heading} className={`px-4 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider ${index === 0 || index >= 3 ? 'text-center' : 'text-left'}`}>
                                                {heading === 'TMA 25%' ? <><span>TMA</span><br /><span>25%</span></> : heading}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {filteredRanking.map(attendant => {
                                        const displayRank = departmentRanks.get(attendant.id) || attendant.rank;
                                        return <tr key={attendant.id} onClick={() => openAttendant(attendant, displayRank)} className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer ${!attendant.isProvisional && displayRank && displayRank <= 3 ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''}`}>
                                            <td className="px-4 py-4 text-center">
                                                {attendant.isProvisional ? (
                                                    <span className="inline-flex px-2 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" title={provisionalReason(attendant)}>PROV.</span>
                                                ) : displayRank <= 3 ? (
                                                    <Medal className={`w-8 h-8 mx-auto ${medalColor(displayRank)}`} fill="currentColor" />
                                                ) : (
                                                    <span className="font-bold text-slate-400">{displayRank}º</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 min-w-64">
                                                <div className="flex items-center gap-3">
                                                    <OpaAvatar name={attendant.name} fotoUrl={attendant.fotoUrl} className="h-10 w-10 text-sm" />
                                                    <div>
                                                        <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">{attendant.name}</div>
                                                        <div className="text-xs text-slate-500 dark:text-slate-400">{attendant.attendancesCount} atendimentos</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 min-w-48">
                                                <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">{attendant.comparisonGroupLabel}</div>
                                                <div className="text-xs text-slate-500 mt-1" title={describeTmaDeviation(attendant.medianTma, attendant.tmaDeviation)}>
                                                    {formatTma(attendant.medianTma)}{formatTmaDeviation(attendant.tmaDeviation) && ` • ${formatTmaDeviation(attendant.tmaDeviation)}`}
                                                </div>
                                                {attendant.comparisonGroupFallback && <div className="text-[10px] text-amber-600 dark:text-amber-400">grupo ampliado por baixa amostra</div>}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span className={`px-3 py-1 inline-flex text-sm font-bold rounded-full border ${getScoreBadge(attendant.qualityScore)}`}>{attendant.qualityScore.toFixed(1)}</span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span className={`px-3 py-1 inline-flex text-sm font-bold rounded-full border ${getScoreBadge(attendant.productivityScore)}`}>{attendant.productivityScore.toFixed(1)}</span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span className={`px-3 py-1 inline-flex text-sm font-bold rounded-full border ${getScoreBadge(attendant.efficiencyScore)}`}>{attendant.efficiencyScore.toFixed(1)}</span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <div className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">{attendant.finalScore.toFixed(2)}</div>
                                                {attendant.qualityGateApplied && <div className="text-[10px] text-red-500" title="Qualidade abaixo do limite mínimo">teto por qualidade</div>}
                                            </td>
                                        </tr>;
                                    })}
                                    {!filteredRanking.length && (
                                        <tr><td colSpan="7" className="px-6 py-8 text-center text-sm text-slate-500">Nenhum atendente corresponde à busca.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {selectedAttendant && (
                <AttendantModal attendant={selectedAttendant} sectorsList={sectorsList} onClose={() => setSelectedAttendant(null)} theme={theme} month={selectedMonth} year={selectedYear} />
            )}
        </div>
    );
};

export default QualityDashboard;
